/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { tap } from 'rxjs/operators';
import { MyInjector } from '@src/app/app.module';
import { Field } from '@src/app/shared/field/Field';
import { SectionEditor } from '@src/app/shared/form/section-editor';
import { Area } from '@src/app/model/area';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { RequestService } from '@src/app/modules/crm/request.service';
import { AreaService } from '@src/app/modules/unit/area.service';
import { uuid } from '@src/app/model/abstract-object';

/**
 * "Edit the people on this request" popup - owner, contact, unit, and where in the development it
 * is. Reported-by ("who logged it") is not editable, so it stays on the page rail and out of here.
 * Unit + Contact come as a pair so changing the unit clears the contact (see
 * ServiceRequest.makeUnitAndContactFields), and Area + Core the same way. Choosing a new contact
 * brings up whether they follow the request, sent with this put only and never stored.
 */
export class RequestPeopleEditor extends SectionEditor<ServiceRequest> {
    protected svc = MyInjector.instance.get(RequestService);
    protected title = $localize`People`;

    private areaSvc = MyInjector.instance.get(AreaService);
    private areas: Area[] = [];

    private ownedByField = ServiceRequest.makeOwnedByField();
    private unitContact = ServiceRequest.makeUnitAndContactFields();
    private areaCore = ServiceRequest.makeAreaAndCoreFields(() => this.areas);

    /**
     * Areas belong to the OMC, not to the agency the page may be standing in, so the request's own
     * team has to come in from the page - see ServiceRequestPageComponent.editPeople.
     */
    constructor(private teamId: uuid) {
        super();
    }

    protected loadRefData() {
        return this.areaSvc.get<Area>(false, null, this.teamId).pipe(
            tap(areas => {
                this.areas = areas;
                this.areaCore.areaField.setPicklistItems(areas);
            })
        );
    }

    protected beforeEdit(sr: ServiceRequest): ServiceRequest {
        this.areaCore.narrowCores(sr.areaId);
        return sr;
    }

    protected buildFields(): Field[] {
        return [
            this.ownedByField,
            this.unitContact.personField,
            this.unitContact.contactLinkField,
            this.unitContact.unitField,
            this.areaCore.areaField,
            this.areaCore.coreField,
        ];
    }
}
